import React, { useEffect, useState } from 'react';
import { Modal, Spinner, Button, Alert, Form, Row, Col } from 'react-bootstrap';
import { CreditCard, AlertTriangle, CheckCircle, Calendar, FileText, Wallet, User } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import * as api from '../../../../services/api/expensesApi';
import { verifikatTableCss } from './verifikatTableStyles';

/**
 * SettleExpenseModal
 *
 * Marks a booked expense as paid. Posts a settlement verifikat that clears
 * the 2891 liability against the chosen payment account (default 1930).
 *
 * The approver picks:
 *   - Settlement date (defaults to today)
 *   - Payment account (default 1930; free-text BAS number — could be 1910
 *     Kassa, or a separate bank 1932, etc.)
 *
 * Guarded at the backend: requires status='booked' and no payroll_id.
 */
const fmt = (n) => new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
const fmtSek = (n) => new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(Number(n) || 0);

const SettleExpenseModal = ({ companyId, expenseId, show, onClose, onSettled }) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [expense, setExpense] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [paymentAccount, setPaymentAccount] = useState('1930');
  const [description, setDescription] = useState('');
  // Kontoplan lookup so the verifikat rows display the tenant's real
  // account names — never invented labels.
  const [accountByNumber, setAccountByNumber] = useState({});
  const nameFor = (num) => accountByNumber[String(num)]?.account_name || '';

  useEffect(() => {
    if (!show || !expenseId) {
      setPreview(null); setExpense(null); setError(null);
      setDate(format(new Date(), 'yyyy-MM-dd'));
      setPaymentAccount('1930');
      setDescription('');
      return;
    }
    let cancelled = false;
    setLoading(true); setError(null);
    (async () => {
      try {
        const [res, accountsRes] = await Promise.all([
          api.previewExpenseSettlement(companyId, expenseId),
          axios.get(getApiUrl(`${companyId}/accounting/accounts`), { withCredentials: true })
            .catch(() => ({ data: { accounts: [] } })),
        ]);
        if (cancelled) return;
        setPreview(res.data?.preview || null);
        setExpense(res.data?.expense || null);
        if (res.data?.preview?.date) setDate(res.data.preview.date.slice(0, 10));
        if (res.data?.preview?.paymentAccount) setPaymentAccount(res.data.preview.paymentAccount);
        if (res.data?.preview?.description) setDescription(res.data.preview.description);
        const byNum = {};
        for (const a of (accountsRes.data?.accounts || [])) {
          byNum[String(a.account_number)] = a;
        }
        setAccountByNumber(byNum);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Kunde inte hämta förhandsvisning.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [show, companyId, expenseId]);

  const handleSettle = async () => {
    if (!paymentAccount.trim()) { setError('Välj betalkonto.'); return; }
    setBusy(true); setError(null);
    try {
      const res = await api.settleExpense(companyId, expenseId, {
        date,
        paymentAccount: paymentAccount.trim(),
        description: description && description.trim() ? description.trim() : undefined,
      });
      toast.success(`Betalt — verifikat ${res.data?.series || 'V'}${res.data?.verificationNumber}.`);
      onSettled?.(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Utbetalning misslyckades.');
      setBusy(false);
    }
  };

  const gross = Number(expense?.amount) || Number(preview?.totals?.gross) || 0;

  return (
    <Modal show={show} onHide={busy ? undefined : onClose} size="lg" centered backdrop={busy ? 'static' : true}>
      <Modal.Header closeButton={!busy}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: '1.125rem', fontWeight: 600, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
            <CreditCard size={18} /> Betala utlägg
          </div>
          {expense && (
            <div style={{ marginTop: 4, fontSize: '0.75rem', color: '#64748b', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 380 }} title={expense.description}>
                {expense.description || '—'}
              </span>
              <span>·</span>
              <span style={{ fontWeight: 600, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                {fmtSek(gross)}
              </span>
              {expense.employee_firstname && (
                <>
                  <span>·</span>
                  <span>{expense.employee_firstname} {expense.employee_lastname || ''}</span>
                </>
              )}
            </div>
          )}
        </div>
      </Modal.Header>

      <Modal.Body>
        {loading && <div className="text-center py-4"><Spinner animation="border" /></div>}

        {!loading && error && (
          <Alert variant="danger" className="mb-3"><AlertTriangle size={14} className="me-1" /> {error}</Alert>
        )}

        {!loading && preview && (
          <>
            {/* Verifikat header — matches ManualBookkeepingEntry.
                (Expense context — utlägg description, belopp, anställd —
                moved up into the modal header.) */}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    <Calendar size={16} className="me-2" />
                    Utbetalningsdatum
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    disabled={busy}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    <Wallet size={16} className="me-2" />
                    Betalkonto (BAS-nr)
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={paymentAccount}
                    onChange={(e) => setPaymentAccount(e.target.value)}
                    placeholder="1930"
                    disabled={busy}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    <FileText size={16} className="me-2" />
                    Beskrivning
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Verifikatets beskrivning"
                    maxLength={255}
                    disabled={busy}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="bk-compact-table">
              <div className="bk-compact-table-header d-flex">
                <div style={{ flex: '3' }}>Konto</div>
                <div style={{ flex: '1', textAlign: 'right' }}>Debet (SEK)</div>
                <div style={{ flex: '1', textAlign: 'right' }}>Kredit (SEK)</div>
              </div>

              <div className="bk-compact-table-row d-flex align-items-center">
                <div style={{ flex: '3' }}>
                  <div className="fw-medium" style={{ fontSize: '0.875rem' }}>
                    <span style={{ fontFamily: "'SF Mono', Monaco, monospace" }}>2891</span>
                    {nameFor('2891') && (
                      <span style={{ color: '#64748b' }}> — {nameFor('2891')}</span>
                    )}
                  </div>
                </div>
                <div style={{ flex: '1', textAlign: 'right' }} className="bk-amount-cell">
                  <span className="fw-medium text-success">{fmtSek(gross)}</span>
                </div>
                <div style={{ flex: '1', textAlign: 'right' }} className="bk-amount-cell">
                  <span className="text-muted">—</span>
                </div>
              </div>

              <div className="bk-compact-table-row d-flex align-items-center">
                <div style={{ flex: '3' }}>
                  <div className="fw-medium" style={{ fontSize: '0.875rem' }}>
                    <span style={{ fontFamily: "'SF Mono', Monaco, monospace" }}>{paymentAccount || '—'}</span>
                    {nameFor(paymentAccount) && (
                      <span style={{ color: '#64748b' }}> — {nameFor(paymentAccount)}</span>
                    )}
                  </div>
                </div>
                <div style={{ flex: '1', textAlign: 'right' }} className="bk-amount-cell">
                  <span className="text-muted">—</span>
                </div>
                <div style={{ flex: '1', textAlign: 'right' }} className="bk-amount-cell">
                  <span className="fw-medium text-primary">{fmtSek(gross)}</span>
                </div>
              </div>

              <div className="bk-compact-table-footer d-flex">
                <div style={{ flex: '3' }}>Summa</div>
                <div style={{ flex: '1', textAlign: 'right' }} className="bk-amount-cell text-success">
                  {fmtSek(gross)}
                </div>
                <div style={{ flex: '1', textAlign: 'right' }} className="bk-amount-cell text-primary">
                  {fmtSek(gross)}
                </div>
              </div>
            </div>

            <div className="bk-balance-indicator bk-balance-good mt-3">
              <CheckCircle size={16} /> Debet och Kredit balanserar
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={onClose} disabled={busy}>Avbryt</Button>
        <Button variant="primary" onClick={handleSettle} disabled={busy || loading || !preview || !paymentAccount.trim()}>
          {busy ? <Spinner size="sm" animation="border" /> : <><CheckCircle size={14} className="me-1" /> Betala</>}
        </Button>
      </Modal.Footer>

      <style>{verifikatTableCss}</style>
    </Modal>
  );
};

export default SettleExpenseModal;
